// src/pages/Home/index.jsx
import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  List, 
  Tag, 
  Avatar, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  Space, 
  Carousel,
  Divider,
  Skeleton,
  Input,
  Pagination
} from 'antd';
import { 
  BookOutlined, 
  EyeOutlined, 
  LikeOutlined, 
  MessageOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FireOutlined,
  TrophyOutlined,
  SearchOutlined
} from '@ant-design/icons';
import AppHeader from '../../components/Header';
import AppFooter from '../../components/Footer';
import { mockPosts } from '../../api/mockData';
import { Link } from 'react-router-dom';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

// 文章卡片组件
const ArticleCard = ({ article, loggedInUser }) => {
  return (
    <Card 
      hoverable
      style={{ marginBottom: 16 }}
      onClick={() => window.location.hash = `#/post/${article.id}`}
    >
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }}>
          <Title level={4} style={{ margin: 0 }}>
            <Link to={`/post/${article.id}`} style={{ color: 'inherit' }}>
              {article.title}
            </Link>
          </Title>
          <Text type="secondary" style={{ display: 'block', margin: '8px 0' }}>
            {article.summary}
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <Tag icon={<ClockCircleOutlined />} color="default">
              {article.date.split(' ')[0]}
            </Tag>
            <Tag icon={<EyeOutlined />} color="default">
              {article.views} 浏览
            </Tag>
            <Tag icon={<LikeOutlined />} color="default">
              {article.likes} 点赞
            </Tag>
            <Tag icon={<MessageOutlined />} color="default">
              {article.comments?.length || 0} 评论
            </Tag>
            <Tag icon={<BookOutlined />} color="processing">
              {article.category}
            </Tag>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {article.tags?.slice(0, 3).map((tag, index) => (
              <Tag key={index} color="blue">
                {tag}
              </Tag>
            ))}
          </div>
        </div>
        <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size="large" 
            style={{ backgroundColor: '#1890ff' }}
          >
            {article.author.charAt(0)}
          </Avatar>
        </div>
      </div>
    </Card>
  );
};

const Home = ({ loggedInUser, onLogout }) => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5); // 每页显示5篇文章

  // 获取热门文章（按浏览量排序）
  const getHotArticles = () => {
    return [...articles]
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  };

  // 获取特色文章（标记为特色的）
  const getFeaturedArticles = () => {
    return articles.filter(article => article.isFeatured);
  };

  useEffect(() => {
    // 模拟数据加载
    setTimeout(() => {
      setArticles(mockPosts);
      setFilteredArticles(mockPosts);
      setLoading(false);
    }, 800);
  }, []);

  // 搜索功能
  useEffect(() => {
    if (!searchTerm) {
      setFilteredArticles(articles);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = articles.filter(article => 
        article.title.toLowerCase().includes(term) ||
        article.summary.toLowerCase().includes(term) ||
        article.tags.some(tag => tag.toLowerCase().includes(term)) ||
        article.author.toLowerCase().includes(term)
      );
      setFilteredArticles(filtered);
      setCurrentPage(1); // 重置到第一页
    }
  }, [searchTerm, articles]);

  // 分页处理
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // 统计数据
  const statsData = {
    totalArticles: articles.length,
    totalViews: articles.reduce((sum, article) => sum + (article.views || 0), 0),
    totalLikes: articles.reduce((sum, article) => sum + (article.likes || 0), 0),
    totalComments: articles.reduce((sum, article) => 
      sum + (article.comments?.length || 0), 0)
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} />
      
      <Content style={{ padding: '20px 50px', marginTop: '64px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* 统计卡片 */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="文章总数"
                  value={statsData.totalArticles}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="总浏览量"
                  value={statsData.totalViews}
                  prefix={<EyeOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="总点赞数"
                  value={statsData.totalLikes}
                  prefix={<LikeOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="总评论数"
                  value={statsData.totalComments}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 搜索栏 */}
          <Card style={{ marginBottom: 24 }}>
            <Search
              placeholder="搜索文章标题、内容、标签或作者..."
              enterButton={<SearchOutlined />}
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={(value) => setSearchTerm(value)}
            />
          </Card>

          {/* 特色文章轮播 */}
          {getFeaturedArticles().length > 0 && (
            <Card 
              title={
                <Title level={3} style={{ margin: 0 }}>
                  <FireOutlined /> 特色推荐
                </Title>
              }
              style={{ marginBottom: 24 }}
            >
              <Carousel autoplay>
                {getFeaturedArticles().map((article, index) => (
                  <div key={`featured-${index}`} style={{ padding: '20px 0' }}>
                    <Card 
                      hoverable
                      style={{ 
                        height: '200px', 
                        background: `linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)`,
                        borderRadius: '10px'
                      }}
                      onClick={() => window.location.hash = `#/post/${article.id}`}
                    >
                      <div style={{ display: 'flex', height: '100%' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Title level={4} style={{ color: '#1d3557', marginBottom: '8px' }}>
                            {article.title}
                          </Title>
                          <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                            {article.summary.substring(0, 100)}...
                          </Text>
                          <Space>
                            <Tag color="blue">{article.category}</Tag>
                            <Text><CalendarOutlined /> {article.date.split(' ')[0]}</Text>
                          </Space>
                        </div>
                        <div style={{ width: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Avatar 
                            size="large" 
                            style={{ backgroundColor: '#1890ff' }}
                          >
                            {article.author.charAt(0)}
                          </Avatar>
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
              </Carousel>
            </Card>
          )}

          <Row gutter={[24, 0]}>
            <Col xs={24} lg={16}>
              {/* 文章列表 */}
              <Card 
                title={
                  <Title level={3} style={{ margin: 0 }}>
                    最新文章
                  </Title>
                }
              >
                {loading ? (
                  <div>
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} active style={{ marginBottom: 16 }} />
                    ))}
                  </div>
                ) : (
                  <>
                    <List
                      dataSource={paginatedArticles}
                      renderItem={(article) => (
                        <List.Item>
                          <ArticleCard article={article} loggedInUser={loggedInUser} />
                        </List.Item>
                      )}
                    />
                    
                    {/* 分页 */}
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredArticles.length}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                      />
                    </div>
                  </>
                )}
              </Card>
            </Col>
            
            <Col xs={24} lg={8}>
              {/* 热门文章 */}
              <Card 
                title={
                  <Title level={3} style={{ margin: 0 }}>
                    <TrophyOutlined /> 热门文章
                  </Title>
                }
                style={{ marginBottom: 24 }}
              >
                <List
                  dataSource={getHotArticles()}
                  renderItem={(article, index) => (
                    <List.Item 
                      key={article.id}
                      style={{ 
                        padding: '8px 0',
                        cursor: 'pointer'
                      }}
                      onClick={() => window.location.hash = `#/post/${article.id}`}
                    >
                      <List.Item.Meta
                        avatar={
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            backgroundColor: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57'][index],
                            color: 'white',
                            fontWeight: 'bold'
                          }}>
                            {index + 1}
                          </div>
                        }
                        title={
                          <Text strong ellipsis={{ tooltip: article.title }}>
                            {article.title}
                          </Text>
                        }
                        description={
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              <EyeOutlined /> {article.views} 浏览
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {article.date.split(' ')[0]}
                            </Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
              
              {/* 分类标签云 */}
              <Card 
                title={
                  <Title level={3} style={{ margin: 0 }}>
                    <BookOutlined /> 分类标签
                  </Title>
                }
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {Array.from(
                    new Set(articles.flatMap(article => article.tags || []))
                  ).map((tag, index) => (
                    <Tag 
                      key={tag} 
                      color={['blue', 'green', 'orange', 'red', 'purple', 'volcano', 'magenta', 'geekblue'][index % 8]}
                      style={{ marginBottom: '8px', cursor: 'pointer' }}
                      onClick={() => setSearchTerm(tag)}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
      
      <AppFooter />
    </Layout>
  );
};

export default Home;